import { normalizeFormFields } from "./formFields";

export const MEGA_MENU_LAYOUT_OPTIONS = {
  HORIZONTAL: "horizontal",
  VERTICAL: "vertical",
};

export const MEGA_MENU_COMPONENT_KINDS = [
  "buttons",
  "forms",
  "carLists",
  "carFilters",
  "maps",
];

const MEGA_MENU_COMPONENT_KIND_SET = new Set(MEGA_MENU_COMPONENT_KINDS);
const COMPONENT_CATALOG_COLLECTIONS = MEGA_MENU_COMPONENT_KINDS.map((kind) => ({
  kind,
  collectionKey: kind,
}));

const clone = (value) => JSON.parse(JSON.stringify(value));
const finite = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};
const clamp = (value, min, max, fallback) =>
  Math.round(Math.min(max, Math.max(min, finite(value, fallback))));
const cssColor = (value, fallback) => {
  const normalized = String(value || "").trim();
  return normalized && normalized.length <= 100 ? normalized : fallback;
};

export const MEGA_MENU_ALIGNMENT_OPTIONS = Object.freeze([
  "left",
  "center",
  "right",
]);
export const MEGA_MENU_SHADOW_OPTIONS = Object.freeze([
  "none",
  "soft",
  "medium",
  "strong",
]);

const MEGA_MENU_SHADOW_STYLES = Object.freeze({
  none: "none",
  soft: "0 8px 24px rgba(15, 23, 42, 0.12)",
  medium: "0 12px 32px rgba(15, 23, 42, 0.18)",
  strong: "0 20px 48px rgba(15, 23, 42, 0.3)",
});

export const MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS = Object.freeze({
  enabled: false,
  width: 1120,
  height: 420,
  padding: 16,
  alignment: "center",
  backgroundColor: "#ffffff",
  backgroundOpacity: 100,
  textColor: "#1f2937",
  borderColor: "#d8dee8",
  borderWidth: 1,
  borderRadius: 8,
  backdropBlur: 0,
  shadow: "medium",
  items: {},
});

export const normalizeMegaMenuItemLayout = (layout = {}) => ({
  x: clamp(layout.x, 0, 4000, 0),
  y: clamp(layout.y, 0, 4000, 0),
  width: clamp(layout.width, 40, 2000, 240),
  height: clamp(layout.height, 32, 1600, 120),
});

export const normalizeMegaMenuCustomLayout = (layout = {}) => {
  const source = layout && typeof layout === "object" ? layout : {};
  const items =
    source.items && typeof source.items === "object" && !Array.isArray(source.items)
      ? Object.entries(source.items).reduce((result, [key, item]) => {
          if (String(key || "").trim()) {
            result[key] = normalizeMegaMenuItemLayout(item);
          }
          return result;
        }, {})
      : {};
  const alignment = MEGA_MENU_ALIGNMENT_OPTIONS.includes(source.alignment)
    ? source.alignment
    : MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.alignment;
  const shadow = MEGA_MENU_SHADOW_OPTIONS.includes(source.shadow)
    ? source.shadow
    : MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.shadow;
  return {
    enabled: source.enabled === true,
    width: clamp(source.width, 320, 1400, MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.width),
    height: clamp(source.height, 160, 1600, MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.height),
    padding: clamp(source.padding, 0, 120, MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.padding),
    alignment,
    backgroundColor: cssColor(
      source.backgroundColor,
      MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.backgroundColor
    ),
    backgroundOpacity: clamp(
      source.backgroundOpacity,
      0,
      100,
      MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.backgroundOpacity
    ),
    textColor: cssColor(
      source.textColor,
      MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.textColor
    ),
    borderColor: cssColor(
      source.borderColor,
      MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.borderColor
    ),
    borderWidth: clamp(
      source.borderWidth,
      0,
      20,
      MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.borderWidth
    ),
    borderRadius: clamp(
      source.borderRadius,
      0,
      120,
      MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.borderRadius
    ),
    backdropBlur: clamp(
      source.backdropBlur,
      0,
      30,
      MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS.backdropBlur
    ),
    shadow,
    items,
  };
};

export const buildMegaMenuSurfaceStyle = (layout = {}) => {
  const normalized = normalizeMegaMenuCustomLayout(layout);
  const background =
    normalized.backgroundOpacity >= 100
      ? normalized.backgroundColor
      : `color-mix(in srgb, ${normalized.backgroundColor} ${normalized.backgroundOpacity}%, transparent)`;
  return {
    background,
    color: normalized.textColor,
    border:
      normalized.borderWidth > 0
        ? `${normalized.borderWidth}px solid ${normalized.borderColor}`
        : "none",
    borderRadius: normalized.borderRadius,
    boxShadow: MEGA_MENU_SHADOW_STYLES[normalized.shadow],
    backdropFilter:
      normalized.backdropBlur > 0
        ? `blur(${normalized.backdropBlur}px)`
        : undefined,
    "--wm-mega-menu-text-color": normalized.textColor,
    "--wm-mega-menu-heading-color": normalized.textColor,
  };
};

export const createMegaMenuId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const createDefaultMegaMenuLink = (overrides = {}) => ({
  id: createMegaMenuId(),
  label: "",
  targetType: "page",
  pageKey: "",
  url: "",
  ...overrides,
});

export const resolveMegaMenuLinkTargetType = (link) => {
  if (link?.targetType === "url" || link?.targetType === "page") {
    return link.targetType;
  }
  return link?.url && !link?.pageKey ? "url" : "page";
};

export const resolveMegaMenuLayout = (layout) => {
  if (layout === MEGA_MENU_LAYOUT_OPTIONS.VERTICAL) {
    return MEGA_MENU_LAYOUT_OPTIONS.VERTICAL;
  }
  return MEGA_MENU_LAYOUT_OPTIONS.HORIZONTAL;
};

export const createDefaultMegaMenuColumn = (overrides = {}) => ({
  id: createMegaMenuId(),
  title: "",
  links: [createDefaultMegaMenuLink()],
  ...overrides,
});

export const buildMegaMenuComponentOptions = (catalog) =>
  COMPONENT_CATALOG_COLLECTIONS.flatMap(({ kind, collectionKey }) =>
    (Array.isArray(catalog?.[collectionKey]) ? catalog[collectionKey] : [])
      .filter(
        (entry) =>
          entry &&
          !entry.deprecated &&
          typeof entry.id === "string" &&
          entry.id.trim() &&
          entry.defaults &&
          typeof entry.defaults === "object"
      )
      .map((entry) => ({
        value: `${kind}:${entry.id}`,
        kind,
        catalogId: entry.id,
        label: entry.label || entry.id,
        entry,
      }))
  );

export const createMegaMenuComponentFromCatalog = ({
  kind,
  entry,
  overrides = {},
}) => {
  if (!MEGA_MENU_COMPONENT_KIND_SET.has(kind) || !entry?.id) return null;
  const snapshot = clone({
    ...(entry.defaults || {}),
    ...(kind === "forms" ? { fields: normalizeFormFields(entry.fields) } : {}),
  });

  return {
    id: createMegaMenuId(),
    kind,
    catalogId: String(entry.id),
    label: entry.label || entry.id,
    snapshot,
    targetType: "page",
    pageKey: "",
    url: "",
    ...overrides,
  };
};

export const normalizeMegaMenuComponent = (component) => {
  if (
    !component ||
    typeof component !== "object" ||
    !MEGA_MENU_COMPONENT_KIND_SET.has(component.kind) ||
    !component.catalogId
  ) {
    return null;
  }

  return {
    id: component.id || createMegaMenuId(),
    kind: component.kind,
    catalogId: String(component.catalogId),
    label: component.label || String(component.catalogId),
    snapshot:
      component.snapshot &&
      typeof component.snapshot === "object" &&
      !Array.isArray(component.snapshot)
        ? clone(component.snapshot)
        : {},
    targetType: resolveMegaMenuLinkTargetType(component),
    pageKey: component.pageKey || "",
    url: component.url || "",
  };
};

export const createDefaultMegaMenu = () => ({
  enabled: false,
  layout: MEGA_MENU_LAYOUT_OPTIONS.HORIZONTAL,
  columns: [],
  components: [],
  customLayout: { ...MEGA_MENU_CUSTOM_LAYOUT_DEFAULTS, items: {} },
});

export const normalizeMegaMenu = (megaMenu) => {
  if (!megaMenu || typeof megaMenu !== "object") {
    return createDefaultMegaMenu();
  }

  return {
    enabled: !!megaMenu.enabled,
    layout: resolveMegaMenuLayout(megaMenu.layout),
    columns: Array.isArray(megaMenu.columns)
      ? megaMenu.columns.map((column) => ({
          id: column.id || createMegaMenuId(),
          title: column.title || "",
          links: Array.isArray(column.links)
            ? column.links.map((link) => ({
                id: link.id || createMegaMenuId(),
                label: link.label || "",
                targetType: resolveMegaMenuLinkTargetType(link),
                pageKey: link.pageKey || "",
                url: link.url || "",
              }))
            : [],
        }))
      : [],
    components: Array.isArray(megaMenu.components)
      ? megaMenu.components.map(normalizeMegaMenuComponent).filter(Boolean)
      : [],
    customLayout: normalizeMegaMenuCustomLayout(megaMenu.customLayout),
  };
};

export const hasVisibleMegaMenu = (megaMenu) => {
  const normalized = normalizeMegaMenu(megaMenu);
  if (!normalized.enabled) return false;
  return (
    normalized.components.length > 0 ||
    normalized.columns.some((column) =>
      column.links.some((link) => link.label || link.pageKey || link.url)
    )
  );
};

export const ensureMegaMenuColumns = (megaMenu) => {
  const normalized = normalizeMegaMenu(megaMenu);
  if (normalized.columns.length > 0) return normalized;
  return { ...normalized, columns: [createDefaultMegaMenuColumn()] };
};
